import {
  createMiddleware,
  defaultSerovalDeserializerPlugins,
  getDefaultSerovalPlugins,
  getSerovalPlugins,
} from '@tanstack/react-start'
import { fromJSON, toJSONAsync } from 'seroval'
import type { SerovalJSON } from 'seroval'

// Forked from `staticFunctionMiddleware` in
// @tanstack/start-static-server-functions@1.167.44 (which depends on
// @tanstack/start-client-core 1.170.39, the version react-start pins). What
// differs from upstream:
// - Results live under a versioned directory,
//   `/__tsr/staticServerFnCache/<STATIC_DATA_VERSION>/<sha1>.json`.
//   The version is a hash of everything that can change the output (content,
//   code, deps; see vite.config.ts), so a tab still running the previous build
//   keeps fetching JSON in the shape its code expects, and unchanged builds
//   reuse the same URLs (uploaded as immutable, like /assets).
// - The client checks the response: a path that wasn't prerendered (the CDN
//   answers with the SPA shell, status 404) throws `NotPrerenderedError`, and
//   other failures throw a plain error, so only the first becomes a 404 page
//   (`orNotFound` in content.ts). Upstream calls `r.json()` on whatever comes
//   back.
// - `node:fs`/`node:path` are imported inside the server half only, so the
//   client bundle never pulls them in.
// The seroval plugins come from @tanstack/react-start's re-export of
// start-client-core, so there is one copy whatever react-start version is
// installed (no direct dependency to keep in step).

/** The client asked for a server function result that wasn't prerendered. */
export class NotPrerenderedError extends Error {
  constructor(url: string) {
    super(`Not prerendered: ${url}`)
    this.name = 'NotPrerenderedError'
  }
}

async function sha1(message: string) {
  const digest = await crypto.subtle.digest(
    'SHA-1',
    new TextEncoder().encode(message),
  )
  return Array.from(new Uint8Array(digest), (b) =>
    b.toString(16).padStart(2, '0'),
  ).join('')
}

const sortKeys = (_key: string, value: unknown) =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? Object.fromEntries(
        Object.entries(value).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)),
      )
    : value

/**
 * Same key as upstream: JSON of the input with object keys sorted (so
 * `{a, b}` and `{b, a}` share a file), made filename-safe.
 */
export const inputKey = (data: unknown) =>
  JSON.stringify(data ?? '', sortKeys)
    .replace(/[/\\?%*:|"<>]/g, '-')
    .replace(/\s+/g, '_')

const cacheUrl = async (functionId: string, data: unknown) =>
  `/__tsr/staticServerFnCache/${import.meta.env.STATIC_DATA_VERSION}/${await sha1(`${functionId}__${inputKey(data)}`)}.json`

/** Fetches one cached result; see the header for what each failure means. */
export async function fetchResult(url: string): Promise<SerovalJSON> {
  const res = await fetch(url)
  // The CDN serves the SPA shell (HTML, status 404) for an unknown path.
  if (res.status === 404 || res.headers.get('content-type')?.includes('html'))
    throw new NotPrerenderedError(url)
  if (!res.ok) throw new Error(`Failed to load ${url}: HTTP ${res.status}`)
  // Untyped boundary: the JSON we wrote in the server half below.
  return res.json()
}

export const staticDataMiddleware = createMiddleware({ type: 'function' })
  .client(async (ctx) => {
    if (
      process.env.NODE_ENV === 'production' &&
      typeof document !== 'undefined'
    ) {
      const url = await cacheUrl(ctx.serverFnMeta.id, ctx.data)
      const response = fromJSON<{ result: unknown; context: object }>(
        await fetchResult(url),
        { plugins: getSerovalPlugins(defaultSerovalDeserializerPlugins) },
      )
      // Short-circuit like upstream: the result stands in for next()'s. The
      // cast is the one unavoidable one: Start's types only let `next()`'s own
      // return value satisfy a middleware.
      const standIn = {
        result: response.result,
        context: Object.assign({}, ctx.context, response.context),
      }
      // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- Start's middleware types only accept next()'s own return value
      return standIn as never
    }
    return ctx.next()
  })
  .server(async (ctx) => {
    const response = await ctx.next()
    // Start reads these at runtime (so does upstream) but doesn't type them;
    // if a Start upgrade moves them, fail the build instead of caching nothing.
    if (!('result' in response) || !('sendContext' in ctx))
      throw new Error('staticDataMiddleware: Start changed its server context')
    const outDir = process.env.TSS_CLIENT_OUTPUT_DIR
    if (process.env.NODE_ENV === 'production' && outDir) {
      const [fs, path] = await Promise.all([
        import('node:fs/promises'),
        import('node:path'),
      ])
      const file = path.join(
        outDir,
        await cacheUrl(ctx.serverFnMeta.id, ctx.data),
      )
      await fs.mkdir(path.dirname(file), { recursive: true })
      const json = await toJSONAsync(
        { result: response.result, context: ctx.sendContext },
        { plugins: getDefaultSerovalPlugins() },
      )
      await fs.writeFile(file, JSON.stringify(json), 'utf-8')
    }
    return response
  })
